/**
 * TV degradation (PLAN §5): Letterboxd has no TV support, so every
 * `letterboxdTrackable === false` entry is excluded from CSV matching
 * entirely and instead surfaced as one grouped line in the import UI, with a
 * single shortcut to mark them watched — never 28 individual taps.
 */

import type { Entry, EntryId, ISODate } from "@schema/catalog.ts";

export interface TvDegradation {
  /** Every TV entry Letterboxd can't represent, in road order. */
  entries: Entry[];
  /**
   * The subset already aired. The shortcut only marks these — it can't know
   * you've watched a season that hasn't come out yet.
   */
  airedEntryIds: EntryId[];
}

function toISODate(date: Date): ISODate {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function tvDegradation(entries: Entry[], now: Date = new Date()): TvDegradation {
  const today = toISODate(now);
  const untrackable = entries
    .filter((e) => !e.availability.letterboxdTrackable)
    .slice()
    .sort((a, b) => a.orderIndex - b.orderIndex);
  const aired = untrackable.filter((e) => e.releaseDate <= today);
  return { entries: untrackable, airedEntryIds: aired.map((e) => e.id) };
}
